import PropTypes from 'prop-types';
import * as yup from 'yup';
import { withFormik, Form } from 'formik';
import {
  Button,
  DropdownSelect,
  EmailInput,
  PhoneInput,
  RadioGroup,
  RadioInput,
  Container,
  Row,
  Col,
  TextInput,
} from '@figure/frontlib-gui';
import { reduxPropTypes } from '@figure/frontlib-onboarding-redux';
import { SECONDARY_LEAVE_MESSAGES, Reasons } from 'shared/constants/leave-survey';

const LeaveSurveyForm = ({ values, errors, touched, handleSubmit, setFieldValue, leaveReasons }) => (
  <Form onSubmit={handleSubmit}>
    <RadioGroup label="We’re sorry to see you go. Why are you leaving?" errorText={touched.payType && errors.payType}>
      {leaveReasons.map((r) => (
        <RadioInput
          inline={false}
          key={r.type}
          id={`reason-${r.type}`}
          label={r.description}
          name="reason"
          checked={values.reason === r.type}
          value={r.type}
          required
          onChange={() => {
            setFieldValue('reason', r.type);
            setFieldValue('message', '');
          }}
        />
      ))}
    </RadioGroup>

    {values.reason === Reasons.NOT_LOOKING_FOR && (
      <DropdownSelect
        id="message-dropdown"
        label="Tell us more"
        name="message"
        value={values.message}
        errorText={touched.message && errors.message}
        onChange={(e) => setFieldValue('message', e.target.value)}
      >
        <option value="" />
        {SECONDARY_LEAVE_MESSAGES.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </DropdownSelect>
    )}

    {values.reason === Reasons.CONFUSED && (
      <Container>
        <Row>
          <Col sm={6}>
            <EmailInput
              id="email"
              label="Email"
              name="email"
              value={values.email}
              errorText={touched.email && errors.email}
              onChange={(e) => setFieldValue('email', e.target.value)}
            />
          </Col>

          <Col sm={6}>
            <PhoneInput
              id="phoneNumber"
              label="Phone number"
              name="phoneNumber"
              isNumericString
              value={values.phoneNumber}
              errorText={touched.phoneNumber && errors.phoneNumber}
              onChange={(value) => setFieldValue('phoneNumber', value)}
              format="+1 (###) ###-####"
            />
          </Col>
        </Row>
      </Container>
    )}

    {values.reason === Reasons.OTHER && (
      <TextInput
        id="message"
        label="Tell us more"
        name="message"
        value={values.message}
        errorText={touched.message && errors.message}
        onChange={(e) => setFieldValue('message', e.target.value)}
      />
    )}

    <Button primary large center style={{ marginTop: '2rem' }}>
      Submit
    </Button>
  </Form>
);

LeaveSurveyForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  leaveReasons: PropTypes.arrayOf(reduxPropTypes.surveyReason).isRequired,
};

export default withFormik({
  enableReinitialize: true,

  mapPropsToValues: () => ({
    reason: '',
    email: '',
    phoneNumber: '',
    message: '',
  }),

  validationSchema: () =>
    yup.object().shape({
      reason: yup.string().required(),
      email: yup.string().when('reason', {
        is: Reasons.CONFUSED,
        then: yup.string().required(),
      }),
      phoneNumber: yup
        .string()
        .when('reason', {
          is: Reasons.CONFUSED,
          then: yup
            .string()
            .required()
            .test('is-phone-number', 'Must be a valid phone number', (value) => !value || value.toString().length === 10)
            .test('is_pos_one_not_one', 'Do not enter “1” as first digit of area code', (value) => value?.toString()[0] !== '1'),
        })
        .label('phone number')
        .min(10),
      message: yup.string().when('reason', {
        is: (val) => val === Reasons.NOT_LOOKING_FOR || val === Reasons.OTHER,
        then: yup.string().required(),
      }),
    }),

  handleSubmit: (values, { props }) => props.handleSubmit(values),
})(LeaveSurveyForm);
